/**
 * Typed, validated access to the VITE_* environment. Centralised so nothing reads import.meta.env
 * directly (and so a missing required var fails loudly at startup, not as a confusing runtime 401).
 */

function required(key: keyof ImportMetaEnv): string {
  const value = import.meta.env[key];
  if (!value) {
    throw new Error(
      `Missing required env var ${key}. Copy .env.example to .env and fill it in ` +
        `(or set VITE_USE_MOCK_API=true to run without a backend).`,
    );
  }
  return value;
}

/** True when the in-browser mock API layer should serve every request (no backend/creds needed). */
export const USE_MOCK_API = import.meta.env.VITE_USE_MOCK_API === "true";

/**
 * LOCAL-DEV ONLY: hit a REAL backend but skip Auth0 — the AuthProvider injects the same static mock
 * session it uses in mock mode, while the API client still talks to VITE_API_URL. Pairs with the
 * backend's AUTH_BYPASS_FOR_LOCAL_DEV flag. Lets us preview the UI against real mirror data with no
 * Auth0 tenant. Independent of USE_MOCK_API (which gates the in-browser MSW network mock).
 */
export const DEV_BYPASS_AUTH = import.meta.env.VITE_DEV_BYPASS_AUTH === "true";

/** Mock /me persona for dev: owner | admin | viewer | custom. Defaults to owner. */
export const MOCK_ROLE = (import.meta.env.VITE_MOCK_ROLE ?? "owner").toLowerCase();

/** Backend origin. The API client appends /api/v1; health is unprefixed. */
export const API_URL = (import.meta.env.VITE_API_URL ?? "http://localhost:8000").replace(/\/$/, "");

interface Auth0Config {
  domain: string;
  clientId: string;
  audience: string;
}

/**
 * Auth0 SPA config. Only required when NOT mocking — in mock mode the app injects a fake session, so
 * a local dev box needs no real Auth0 tenant.
 */
export function getAuth0Config(): Auth0Config {
  return {
    domain: required("VITE_AUTH0_DOMAIN"),
    clientId: required("VITE_AUTH0_CLIENT_ID"),
    audience: required("VITE_AUTH0_AUDIENCE"),
  };
}
